"use client";
import React, { useState, useEffect, useRef } from "react";
import { FileText, Pencil, Check, X, Paperclip, Trash2 } from "lucide-react";
import { smartAxios } from "../../api/axios/Axios";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PrimaryButton } from "../../components/buttons/primaryButton/PrimaryButton";

const MAX_CHARS = 1000;

export default function YourBusinessDescription() {
  const [description, setDescription] = useState("");
  const { t } = useTranslation();
  const [savedDescription, setSavedDescription] = useState("");
  const [fullName, setFullName] = useState(""); // required by PUT
  const [serverFile, setServerFile] = useState(null); // URL string saved on server
  const [pendingFile, setPendingFile] = useState(null); // File object selected locally (not yet saved)
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingFile, setDeletingFile] = useState(false);
  const [visible, setVisible] = useState(true); // hidden for workers
  const fileRef = useRef(null);

  // ── Fetch current description ────────────────────────────────────────────────

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await smartAxios.get("/auth/userinfo/");
        const desc = res.data?.business_description || "";
        const file = res.data?.business_description_file || null;
        const status = res.data?.user?.status || null;
        const name =
          res.data?.full_name ||
          `${res.data?.user?.first_name || ""} ${res.data?.user?.last_name || ""}`.trim();
        if (status === "worker") {
          setVisible(false);
          return;
        }
        setFullName(name);
        setDescription(desc);
        setSavedDescription(desc);
        setServerFile(file);
      } catch {
        // silently fail — component just shows empty state
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const cancelEdit = () => {
    setDescription(savedDescription);
    setPendingFile(null);
    setEditing(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
  };

  // If file is only selected locally (not yet saved) — just clear state.
  // If file exists on server — call DELETE /auth/userinfo/ to remove it.
  const handleRemoveFile = async () => {
    if (pendingFile) {
      // locally selected, not yet saved
      setPendingFile(null);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    // file is on server — delete via API
    try {
      setDeletingFile(true);
      await smartAxios.delete("/auth/userinfo/");
      setServerFile(null);
    } catch {
      toast.error(t("settings.failedToDelete"));
    } finally {
      setDeletingFile(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const current = await smartAxios.get("/auth/userinfo/");
      const data = current.data || {};
      const user = data.user || {};

      const first = user.first_name || "";
      const last = user.last_name || "";
      const full = data.full_name || `${first} ${last}`.trim();
      const userType = user.status || "individual";
      const workPlaceIds = Array.isArray(data.work_place)
        ? data.work_place.map((p) => p.id)
        : [];

      const fd = new FormData();
      fd.append("full_name", full);
      fd.append(
        "company_name",
        userType === "salon" ? user.company_name || "" : "",
      );
      fd.append("phone_number", data.phone_number || "");
      fd.append("address_url", data.address_url || "");
      fd.append("currency", data.currency || "");
      workPlaceIds.forEach((id) => fd.append("work_place_ids", id));

      fd.append("business_description", description);
      if (pendingFile) {
        fd.append("business_description_file", pendingFile);
      }

      const res = await smartAxios.put("/auth/userinfo/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const updated = res.data;
      setSavedDescription(updated?.business_description || description);
      setServerFile(updated?.business_description_file || null);
      setPendingFile(null);
      setEditing(false);
      toast.success(t("settings.descriptionUpdated"));
    } catch {
      toast.error(t("settings.failedToSave"));
    } finally {
      setSaving(false);
    }
  };

  // ── Derived display values ───────────────────────────────────────────────────
  // Show pending (local) file first, then server file
  const displayFile = pendingFile
    ? { name: pendingFile.name, isPending: true }
    : serverFile
      ? { name: serverFile.split("/").pop(), url: serverFile, isPending: false }
      : null;

  // ── Render ───────────────────────────────────────────────────────────────────

  if (!visible) return null;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col gap-4 sm:gap-6 relative border border-gray-100">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between sm:items-center items-start relative z-10">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-[#020637] rounded-xl shadow-sm flex-shrink-0">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-bold text-xl text-gray-900">
              {t("settings.businessDescription")}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {t("settings.description")}
            </p>
          </div>
        </div>

        {!editing ? (
          <PrimaryButton
            onClick={() => setEditing(true)}
            className="w-full sm:w-auto"
            label={t("settings.edit")}
            rightIcon={<Pencil className="w-4 h-4" />}
          />
        ) : (
          <div className="flex gap-2 w-full sm:w-auto">
            <PrimaryButton
              onClick={cancelEdit}
              disabled={saving}
              variant="outline"
              leftIcon={<X className="w-4 h-4" />}
              label={t("settings.cancel")}
            />
            {(description.length > 0 || displayFile?.name) && (
              <PrimaryButton
                onClick={handleSave}
                disabled={saving}
                leftIcon={
                  saving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )
                }
                label={t("settings.save")}
              />
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="relative z-10 space-y-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-6">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-[#020637] rounded-full animate-spin mb-3" />
            <p className="text-sm text-gray-500">{t("settings.loading")}</p>
          </div>
        ) : editing ? (
          <>
            {/* Text area */}
            <div className="relative">
              <textarea
                value={description}
                onChange={(e) => {
                  if (e.target.value.length <= MAX_CHARS)
                    setDescription(e.target.value);
                }}
                maxLength={MAX_CHARS}
                rows={5}
                placeholder={t("settings.descriptionPlaceholder")}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#020637] focus:ring-2 focus:ring-[#020637]/15 resize-none transition-all"
              />
              <span className="absolute bottom-3 right-3 text-xs text-gray-400">
                {description.length} / {MAX_CHARS}
              </span>
            </div>

            {/* File upload */}
            <div>
              <p className="text-xs font-medium text-gray-600 mb-2">
                {t("settings.descriptionFile")}
              </p>
              {displayFile ? (
                <div className="flex items-center gap-3 px-4 py-3 bg-[#020637]/5 border border-[#020637]/15 rounded-xl">
                  <Paperclip className="w-4 h-4 text-[#020637] shrink-0" />
                  <span className="text-sm text-gray-700 truncate grow">
                    {displayFile.name}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    disabled={deletingFile}
                    className="shrink-0 p-1 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                  >
                    {deletingFile ? (
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-red-400 rounded-full animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl hover:border-[#020637] hover:bg-[#020637]/8 transition-all text-sm text-gray-500 hover:text-[#020637]"
                >
                  <Paperclip className="w-4 h-4 shrink-0" />
                  <span>{t("settings.uploadFile")}</span>
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx,.csv,.txt,.xls,.xlsx"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </>
        ) : (
          <>
            {/* View mode — description text */}
            {savedDescription ? (
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {savedDescription}
              </p>
            ) : (
              <div className="py-4 text-center">
                <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500 font-medium">
                  {t("settings.noDescriptionYet")}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {t("settings.clickEditToAdd")}
                </p>
              </div>
            )}

            {/* View mode — attached file */}
            {serverFile && (
              <a
                href={serverFile}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 hover:border-[#020637] hover:bg-[#020637]/8 hover:text-[#020637] transition-all"
              >
                <Paperclip className="w-4 h-4 shrink-0" />
                <span className="truncate max-w-xs">
                  {serverFile.split("/").pop()}
                </span>
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
}
